/**
 * Session 12 / CR 2026-05-27 §4.1 — extracted out of `useStationStore.ts`.
 *
 * Routes CSMS-initiated CALL frames to the right side effect + reply. Tech-debt
 * #4 (Phase C) decomposed what used to be a ~960-line switch into per-family
 * modules under `./csms/`; this file is now a thin dispatcher that parses the
 * incoming frame, builds a shared `CsmsCtx`, and delegates each narrowed event
 * to its family handler. Shared repos + Device-Model report helpers live in
 * `./csms/shared.ts`.
 */
import type { StationId } from '@/domain/types';
import type { OcppAdapter } from '@/protocol/OcppAdapter';
import type { OcppCallFrame } from '@/protocol/OcppCall';
import type { CallDispatcher } from '@/protocol';
import type { CsmsCtx } from './csms/context';
import { handleChangeAvailability } from './csms/availability';
import {
  handleCertificateSigned,
  handleDeleteCertificate,
  handleGetInstalledCertificateIds,
  handleInstallCertificate,
} from './csms/certificates';
import {
  handleChangeConfiguration,
  handleGetBaseReport,
  handleGetConfiguration,
  handleGetReport,
  handleGetVariables,
  handleReset,
  handleSetVariables,
} from './csms/deviceModel';
import {
  handleClearDisplayMessage,
  handleGetDisplayMessages,
  handleSetDisplayMessage,
} from './csms/displayMessages';
import {
  handleGetDiagnostics,
  handleGetLog,
  handlePublishFirmware,
  handleUnpublishFirmware,
  handleUpdateFirmware,
} from './csms/firmware';
import { handleGetLocalListVersion, handleSendLocalList } from './csms/localAuthList';
import { handleCostUpdated } from './csms/misc';
import {
  handleClearVariableMonitoring,
  handleGetMonitoringReport,
  handleSetMonitoringBase,
  handleSetMonitoringLevel,
  handleSetVariableMonitoring,
} from './csms/monitoring';
import { handleCustomerInformation, handleSetNetworkProfile } from './csms/network';
import { handleCancelReservation, handleReserveNow } from './csms/reservations';
import {
  handleClearChargingProfile,
  handleGetChargingProfiles,
  handleGetCompositeSchedule,
  handleSetChargingProfile,
} from './csms/smartCharging';
import {
  handleClearCache,
  handleDataTransfer,
  handleGetTransactionStatus,
  handleRemoteStartTransaction,
  handleRemoteStopTransaction,
  handleUnlockConnector,
} from './csms/transactions';
import { handleTriggerMessage } from './csms/triggerMessage';

export async function handleCsmsCall(
  frame: OcppCallFrame,
  adapter: OcppAdapter,
  dispatcher: CallDispatcher,
  stationId: StationId,
): Promise<void> {
  const event = adapter.parseIncoming(frame);
  const messageId = frame[1];
  // Invalid (from the defensive parser): reply CALLERROR per OCPP 1.6 §4.2.
  // `FormationViolation` is reserved for malformed JSON frames (caught in
  // `CallDispatcher.feed`); semantic-range failures are
  // `PropertyConstraintViolation` per OCPP 1.6J §4.2.
  if (event.kind === 'Invalid') {
    dispatcher.replyError(messageId, 'PropertyConstraintViolation', event.reason, {
      originalAction: event.originalAction,
    });
    return;
  }
  // Tech-debt #4 — shared context handed to the per-family handler modules.
  const ctx: CsmsCtx = { adapter, dispatcher, stationId, messageId };
  switch (event.kind) {
    // ── Local Authorization List ──────────────────────────────────────────
    case 'GetLocalListVersion':
      return handleGetLocalListVersion(event, ctx);
    case 'SendLocalList':
      return handleSendLocalList(event, ctx);

    // ── Device Model / configuration ──────────────────────────────────────
    case 'Reset':
      return handleReset(event, ctx);
    case 'ChangeConfiguration':
      return handleChangeConfiguration(event, ctx);
    case 'GetConfiguration':
      return handleGetConfiguration(event, ctx);
    case 'GetVariables':
      return handleGetVariables(event, ctx);
    case 'SetVariables':
      return handleSetVariables(event, ctx);
    case 'GetBaseReport':
      return handleGetBaseReport(event, ctx);
    case 'GetReport':
      return handleGetReport(event, ctx);

    // ── Availability ──────────────────────────────────────────────────────
    case 'ChangeAvailability':
      return handleChangeAvailability(event, ctx);

    // ── Reservations ──────────────────────────────────────────────────────
    case 'ReserveNow':
      return handleReserveNow(event, ctx);
    case 'CancelReservation':
      return handleCancelReservation(event, ctx);

    // ── Smart charging ────────────────────────────────────────────────────
    case 'ClearChargingProfile':
      return handleClearChargingProfile(event, ctx);
    case 'SetChargingProfile':
      return handleSetChargingProfile(event, ctx);
    case 'GetCompositeSchedule':
      return handleGetCompositeSchedule(event, ctx);
    case 'GetChargingProfiles':
      return handleGetChargingProfiles(event, ctx);

    // ── Trigger message ───────────────────────────────────────────────────
    case 'TriggerMessage':
      return handleTriggerMessage(event, ctx);

    // ── Remote control / transactions ─────────────────────────────────────
    case 'UnlockConnector':
      return handleUnlockConnector(event, ctx);
    case 'RemoteStartTransaction':
      return handleRemoteStartTransaction(event, ctx);
    case 'RemoteStopTransaction':
      return handleRemoteStopTransaction(event, ctx);
    case 'DataTransfer':
      return handleDataTransfer(event, ctx);
    case 'ClearCache':
      return handleClearCache(event, ctx);
    case 'GetTransactionStatus':
      return handleGetTransactionStatus(event, ctx);

    // ── Display messages ──────────────────────────────────────────────────
    case 'SetDisplayMessage':
      return handleSetDisplayMessage(event, ctx);
    case 'ClearDisplayMessage':
      return handleClearDisplayMessage(event, ctx);
    case 'GetDisplayMessages':
      return handleGetDisplayMessages(event, ctx);

    // ── Firmware / diagnostics / log ──────────────────────────────────────
    case 'GetLog':
      return handleGetLog(event, ctx);
    case 'UpdateFirmware':
      return handleUpdateFirmware(event, ctx);
    case 'PublishFirmware':
      return handlePublishFirmware(event, ctx);
    case 'UnpublishFirmware':
      return handleUnpublishFirmware(event, ctx);
    case 'GetDiagnostics':
      return handleGetDiagnostics(event, ctx);

    // ── Variable monitoring ───────────────────────────────────────────────
    case 'SetVariableMonitoring':
      return handleSetVariableMonitoring(event, ctx);
    case 'ClearVariableMonitoring':
      return handleClearVariableMonitoring(event, ctx);
    case 'GetMonitoringReport':
      return handleGetMonitoringReport(event, ctx);
    case 'SetMonitoringBase':
      return handleSetMonitoringBase(event, ctx);
    case 'SetMonitoringLevel':
      return handleSetMonitoringLevel(event, ctx);

    // ── Security / certificates ───────────────────────────────────────────
    case 'CertificateSigned':
      return handleCertificateSigned(event, ctx);
    case 'InstallCertificate':
      return handleInstallCertificate(event, ctx);
    case 'DeleteCertificate':
      return handleDeleteCertificate(event, ctx);
    case 'GetInstalledCertificateIds':
      return handleGetInstalledCertificateIds(event, ctx);

    // ── Network / customer info ───────────────────────────────────────────
    case 'SetNetworkProfile':
      return handleSetNetworkProfile(event, ctx);
    case 'CustomerInformation':
      return handleCustomerInformation(event, ctx);

    // ── Misc ──────────────────────────────────────────────────────────────
    case 'CostUpdated':
      return handleCostUpdated(event, ctx);
    case 'Unknown':
      dispatcher.replyError(messageId, 'NotImplemented', `unknown action: ${event.action}`, {});
      return;
  }
}
